import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Container, Row} from "react-bootstrap";
import {useNavigate, useParams} from "react-router-dom";
import classes from "./WebUserDetailPage.module.css";
import PageHeaderName from "@/components/PageHeaderName/PageHeaderName";
import KeyFeatures from "@/components/KeyFeatures";
import {webUserMutationApi, webUsersApi} from "@/apis/apis";
import {useGet} from "@/apiHooks/HttpFunctions";
import {userStatusBgColors, userStatusLabels} from "@/constant/Users/status";
import TagBadge from "@/components/TagBadge";
import {toTitleCase} from "@/utils/helper";
import {
  ACTION_PROMOTE_TO_PARTNER,
  ACTION_REMOVE_PERMANENTLY, ACTION_SET_PLATFORM_FEE,
  WEB_USER_EDIT_ACTIONS
} from "@/pages/UserManagement/WebUserDetailPage/actions";
import WebUserMutationModal from "@/Modals/WebUserMutation";
import UserPlatformFeeModal from "@/Modals/UserPlatformFeeModal";

export default function WebUserDetailPage() {
  const {slug} = useParams();
  const navigate = useNavigate();
  const [loadingWithType, setLoadingWithType] = useState(defaultLoader);
  const [userData, setUserData] = useState(null);
  const initialModalState = useMemo(() => ({
    isLoading: false,
    isOpen: false,
    header: '',
    text: '',
    actionKey: '',
    requireReason: true,
    allowReference: true,
    endpointUrl: '',
  }), []);
  const [mutationModalState, setMutationModalState] = useState(initialModalState);
  const [feeModalState, setFeeModalState] = useState({isOpen: false});

  const {data: responseData, error: showError} = useGet({
    apiURL: `${webUsersApi}/${slug}`
  });

  const closeModal = useCallback(() => {
    setFeeModalState(initialModalState);
  }, [initialModalState]);

  useEffect(() => {
    if (responseData) {
      setUserData(responseData.data);
    }
  }, [responseData]);

  const openModal = useCallback((actionItem) => {
    if (actionItem.key === ACTION_SET_PLATFORM_FEE) {
      setFeeModalState({isOpen: true});
    } else {
      setFeeModalState({
        isLoading: false,
        isOpen: true,
        header: actionItem.title,
        text: actionItem.confirmationText,
        actionKey: actionItem.key,
        requireReason: actionItem.key !== ACTION_PROMOTE_TO_PARTNER,
        allowReference: actionItem.key !== ACTION_PROMOTE_TO_PARTNER,
        endpointUrl: `${webUserMutationApi}${actionItem.endpointPath}`,
      });
    }
  }, []);

  const onSuccess = useCallback(() => {
    if (feeModalState.actionKey === ACTION_REMOVE_PERMANENTLY) {
      navigate('/user-management/web-user-list');
    }
  }, [feeModalState.actionKey, navigate]);

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <PageHeaderName title={"Edit Web User"}/>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <h3 className="h3 py-3">
            <span className="border-b border-solid border-black py-1">
              {userData?.first_name + ' ' + userData?.last_name}&nbsp;|&nbsp;
              <img src={userData?.primary_address?.country.icon_url} alt={'flag'}
                   className="d-inline border border-solid border-b-gray-50"
                   style={{height: '0.8em', marginBottom: '3px'}}/> {userData?.primary_address?.country.name}
            </span>
          </h3>
          <div className={`flex ${classes.flexContainer}`}>
            <TagBadge bgColor={userStatusBgColors[userData?.status]} key={'status'}>
              {userStatusLabels[userData?.status]}
            </TagBadge>
            {userData?.roles.map(role => (
              <TagBadge key={role.name}>
                {toTitleCase(role.name)}
              </TagBadge>
            ))}
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div className={classes.listFeaturesDiv}>
            {WEB_USER_EDIT_ACTIONS.map((e, i) => (
              e.enable(userData) && <div className={classes.keyFeaturesDiv} key={i}>
                <KeyFeatures
                  data={e}
                  onClick={openModal}
                  loadingWithType={loadingWithType}
                />
              </div>
            ))}
          </div>
        </Col>
      </Row>
      <WebUserMutationModal
        headerText={feeModalState.header}
        confirmationText={feeModalState.text}
        isOpen={feeModalState.isOpen}
        userId={Number(slug)}
        onHide={closeModal}
        onSuccess={onSuccess}
        requireReason={feeModalState.requireReason}
        allowReference={feeModalState.allowReference}
        endpointUrl={feeModalState.endpointUrl}
      />
      <UserPlatformFeeModal
        headerText="Set Platform Fee"
        userId={Number(slug)}
        isOpen={feeModalState.isOpen}
        onCancel={() => setFeeModalState({isOpen: false})}
        onSuccess={() => setFeeModalState({isOpen: false})}
      />
    </Container>
  );
}

const defaultLoader = {
  title: "",
  loader: "", // deleteUser, resetPassword
};
